import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { SITE, SPONSOR } from "@/lib/site";

const URL = `${SITE.url}/sponsors/gbply`;
const LAST_UPDATED = "August 2026";

export const Route = createFileRoute("/sponsors/gbply")({
  head: () => ({
    meta: [
      { title: `GBPly — our sponsor and funder | ${SITE.name}` },
      {
        name: "description",
        content:
          "GBPly founded and funds Map Observer. Who they are, what the sponsorship pays for, and the editorial firewall that keeps our reporting independent.",
      },
      { property: "og:title", content: "GBPly — our sponsor and funder" },
      {
        property: "og:description",
        content:
          "GBPly — A Smarter Way to Manage Google Business Profiles — funds Map Observer. Full disclosure of the relationship and our editorial firewall.",
      },
      { property: "og:url", content: URL },
      { property: "og:type", content: "article" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AboutPage",
          "@id": `${URL}#page`,
          url: URL,
          name: "GBPly — our sponsor and funder",
          isPartOf: { "@id": `${SITE.url}/#organization` },
          about: {
            "@type": "Organization",
            name: SPONSOR.name,
            url: SPONSOR.url,
            slogan: SPONSOR.tagline,
            description:
              "GBPly is a software platform for managing Google Business Profiles. It founded and funds Map Observer.",
          },
        }),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-3xl px-5 sm:px-8 py-12">
        <p className="eyebrow">Sponsor disclosure</p>
        <h1 className="headline-display text-4xl mt-3">
          GBPly — our sponsor and funder
        </h1>
        <p className="mt-4 text-sm text-muted-foreground">
          Last updated: {LAST_UPDATED}
        </p>

        <section className="mt-10 space-y-4 text-sm leading-relaxed">
          <p className="text-base">
            Map Observer exists because of{" "}
            <a
              href={SPONSOR.url}
              rel={SPONSOR.rel}
              className="font-medium text-ochre-deep underline underline-offset-4 hover:text-accent"
            >
              GBPly
            </a>
            . They founded this publication, they fund it, and they are its sole
            source of revenue. We say that here, in the footer of every page, and
            at the bottom of every article, because a newsroom that hides who pays
            for it is not worth reading.
          </p>

          <h2 className="headline-display text-2xl mt-8">Who GBPly is</h2>
          <p>
            GBPly is a software platform for managing Google Business Profiles,
            built around a simple proposition:{" "}
            <em>{SPONSOR.tagline}</em>. It is aimed at the same people this
            publication is written for — local SEO agencies managing profiles at
            scale, GBP specialists, and multi-location operators who need their
            listings accurate, consistent, and defensible across every location
            they run.
          </p>
          <p>
            The full product, pricing, and feature detail lives on their own site
            at{" "}
            <a
              href={SPONSOR.url}
              rel={SPONSOR.rel}
              className="text-ochre-deep underline underline-offset-4 hover:text-accent"
            >
              {SPONSOR.domain}
            </a>
            . We deliberately do not maintain a feature list here — this is a
            disclosure page, not a sales page, and describing their roadmap as if
            it were our reporting would blur exactly the line we are trying to keep
            sharp.
          </p>

          <h2 className="headline-display text-2xl mt-8">
            What the sponsorship pays for
          </h2>
          <ul className="list-disc pl-5 space-y-1">
            <li>Editorial production: reporting, editing, and fact-checking.</li>
            <li>
              Infrastructure: hosting, monitoring, source ingestion, and the AI
              tooling described in our{" "}
              <Link to="/editorial-standards" className="text-accent underline">
                editorial standards
              </Link>
              .
            </li>
            <li>
              The free reader products — the daily briefing, the newsletter, the
              trackers, the reference tools, the public API, and the embeds — all
              of which stay free and unpaywalled.
            </li>
          </ul>

          <h2 className="headline-display text-2xl mt-8">
            What the sponsorship does not buy
          </h2>
          <p>
            The commercial relationship stops at the door of the newsroom. GBPly
            does not:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Select, commission, or kill stories.</li>
            <li>Review, approve, or amend copy before publication.</li>
            <li>See our reporting ahead of readers.</li>
            <li>
              Receive softer treatment than any other vendor, or influence how we
              cover their competitors.
            </li>
          </ul>
          <p>
            If GBPly ships a change, gets something wrong, or is affected by a
            Google policy shift, we cover it on the same standard we apply to every
            other company in this industry. If we ever cannot report a story
            honestly because of who funds us, we will say so in place of the story.
          </p>

          <h2 className="headline-display text-2xl mt-8">
            How you will always be able to tell
          </h2>
          <ul className="list-disc pl-5 space-y-1">
            <li>
              Every article that mentions GBPly or a direct competitor carries an
              inline disclosure at the point of mention.
            </li>
            <li>
              Sponsor placements are labelled &ldquo;Sponsored&rdquo; and are
              visually separate from editorial content.
            </li>
            <li>
              Links to GBPly carry <code>rel=&quot;sponsored&quot;</code>, the
              attribution search engines expect for a funded relationship.
            </li>
            <li>
              We have never published, and will not publish, an advertorial or a
              paid article dressed as reporting.
            </li>
          </ul>

          <p className="mt-8">
            The full disclosure, including how this changed when GBPly took over
            funding, is on our{" "}
            <Link to="/ownership-and-funding" className="text-accent underline">
              ownership and funding
            </Link>{" "}
            page. Our{" "}
            <Link to="/ethics" className="text-accent underline">
              ethics policy
            </Link>{" "}
            covers conflicts of interest, and{" "}
            <Link to="/corrections" className="text-accent underline">
              corrections
            </Link>{" "}
            covers what happens when we get something wrong.
          </p>

          <div className="mt-10 border-t border-border pt-6">
            <p className="eyebrow">Sponsored</p>
            <p className="mt-2 text-base">
              <a
                href={SPONSOR.url}
                rel={SPONSOR.rel}
                className="font-medium text-ochre-deep underline underline-offset-4 hover:text-accent"
              >
                GBPly
              </a>{" "}
              <span className="text-muted-foreground">— {SPONSOR.tagline}.</span>
            </p>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
